import { useState } from "react";
import { plannerAPI } from "../utils/api";
import MarkdownRenderer from "../components/MarkdownRenderer";

const CITIES = [
  "Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Chennai", "Pune", "Kolkata",
  "Ahmedabad", "Surat", "Jaipur", "Lucknow", "Kanpur", "Nagpur", "Indore",
  "Thane", "Bhopal", "Visakhapatnam", "Pimpri-Chinchwad", "Patna", "Vadodara",
  "Coimbatore", "Ludhiana", "Agra", "Madurai", "Nashik", "Faridabad",
  "Meerut", "Rajkot", "Vasai-Virar", "Aurangabad", "Other / Rural",
];

const SOIL_TYPES = [
  "Normal Red / Sandy Loam (SBC > 150 kN/m²)",
  "Black Cotton Soil (Expansive Clay - Raft / Under-reamed Pile needed)",
  "Hard Rock Strata (SBC > 300 kN/m²)",
  "Alluvial / Riverbed Soil (Waterlogged / High water table)",
];

const AMENITY_OPTIONS = [
  { id: "solar", label: "☀️ Rooftop Solar PV (3kW)", defaultChecked: true },
  { id: "rwh", label: "🌧️ Rainwater Harvesting Sump", defaultChecked: true },
  { id: "ev", label: "⚡ EV Charging Point in Parking", defaultChecked: false },
  { id: "elderly", label: "🧓 Elderly-friendly Ground Floor Bedroom", defaultChecked: true },
  { id: "vastu", label: "🧭 100% Vastu Shastra Compliant Layout", defaultChecked: true },
  { id: "modular_kitchen", label: "🍳 Modular Kitchen with Chimney Duct", defaultChecked: true },
];

export default function PlannerPage({ onAskAI }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    plot_area: 1500,
    floors: "2",
    city: "Nagpur",
    budget: "35-40 Lakhs",
    style: "Modern Indian",
    soil_type: SOIL_TYPES[0],
    requirements: "Parent bedroom on ground floor, 3kW solar system, Vastu compliance, 2 car parking.",
  });

  const [selectedAmenities, setSelectedAmenities] = useState(["solar", "rwh", "elderly", "vastu"]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggleAmenity = (id) => {
    setSelectedAmenities((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleGenerate = async () => {
    if (!form.plot_area || !form.city) {
      setError("Plot area and city are required.");
      return;
    }
    setError(null);
    setLoading(true);
    setReport(null);

    const amenitiesSummary = selectedAmenities
      .map((id) => AMENITY_OPTIONS.find((a) => a.id === id)?.label)
      .filter(Boolean)
      .join(", ");

    const combinedReqs = `${form.requirements}. Soil: ${form.soil_type}. Amenities: ${amenitiesSummary}`;

    try {
      const payload = {
        plot_area: parseFloat(form.plot_area),
        floors: parseInt(form.floors),
        city: form.city,
        budget: form.budget,
        style: form.style,
        requirements: combinedReqs,
      };
      const data = await plannerAPI.generateReport(payload);
      setReport(data.report);
      setStep(3); // Jump to report view
    } catch (err) {
      setError(`Error generating report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="tool-page-container">
      <div className="page-header">
        <div className="header-badge">📋 PROJECT MASTER PLANNER</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "10px" }}>
          <div>
            <h1>Comprehensive House Planning Master 🏗️</h1>
            <p>Enter plot specifications, soil type, and Vastu requirements to generate a complete IS-code certified report.</p>
          </div>
          {report && (
            <button className="action-pill-btn" onClick={handlePrint}>
              🖨️ Print Master Report
            </button>
          )}
        </div>
      </div>

      {/* Stepper Navigation */}
      <div className="planner-stepper-bar">
        <div className={`step-item ${step >= 1 ? "active" : ""}`} onClick={() => setStep(1)}>
          <span className="step-num">1</span>
          <span className="step-title">Site &amp; Layout</span>
        </div>
        <div className="step-connector" />
        <div className={`step-item ${step >= 2 ? "active" : ""}`} onClick={() => setStep(2)}>
          <span className="step-num">2</span>
          <span className="step-title">Style &amp; Amenities</span>
        </div>
        <div className="step-connector" />
        <div className={`step-item ${step >= 3 ? "active" : ""}`} onClick={() => report && setStep(3)}>
          <span className="step-num">3</span>
          <span className="step-title">Generated Master Plan</span>
        </div>
      </div>

      {/* Step 1: Site & Structure */}
      {step === 1 && (
        <div className="card planner-step-card">
          <h2 className="card-title">📐 Step 1: Site &amp; Structural Dimensions</h2>
          <p className="card-subtitle">Enter your plot area, number of floors, and location:</p>

          <div className="form-grid-2col">
            <div className="form-group-block">
              <label className="field-label">Plot / Built-up Area (sq ft) *</label>
              <input
                type="number"
                name="plot_area"
                className="form-input-styled"
                value={form.plot_area}
                onChange={handleChange}
                placeholder="e.g. 1500"
                min="200"
              />
            </div>

            <div className="form-group-block">
              <label className="field-label">Number of Floors *</label>
              <select className="form-select-styled" name="floors" value={form.floors} onChange={handleChange}>
                <option value="1">G (Ground floor only)</option>
                <option value="2">G+1 (2 floors / Duplex)</option>
                <option value="3">G+2 (3 floors)</option>
                <option value="4">G+3 (4 floors)</option>
                <option value="5">G+4 (5 floors)</option>
              </select>
            </div>

            <div className="form-group-block">
              <label className="field-label">City / Location *</label>
              <select className="form-select-styled" name="city" value={form.city} onChange={handleChange}>
                {CITIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group-block">
              <label className="field-label">Soil &amp; Strata Condition</label>
              <select className="form-select-styled" name="soil_type" value={form.soil_type} onChange={handleChange}>
                {SOIL_TYPES.map((s, idx) => (
                  <option key={idx} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="step-actions-row">
            <button className="primary-brand-btn" onClick={() => setStep(2)}>
              Next: Style &amp; Amenities ➔
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Architecture & Amenities */}
      {step === 2 && (
        <div className="card planner-step-card">
          <h2 className="card-title">🎨 Step 2: Architectural Style &amp; Custom Features</h2>
          <p className="card-subtitle">Select budget, design style, and key amenities:</p>

          <div className="form-grid-2col">
            <div className="form-group-block">
              <label className="field-label">Target Budget (₹ Lakhs, optional)</label>
              <input
                type="text"
                name="budget"
                className="form-input-styled"
                value={form.budget}
                onChange={handleChange}
                placeholder="e.g. 30-35 Lakhs"
              />
            </div>

            <div className="form-group-block">
              <label className="field-label">Construction &amp; Elevation Style</label>
              <select className="form-select-styled" name="style" value={form.style} onChange={handleChange}>
                <option value="Modern Indian">Modern Indian (Contemporary Facade)</option>
                <option value="Traditional / Vastu-compliant">Traditional Vastu-Compliant (Courtyard/Verandah)</option>
                <option value="Contemporary Minimalist">Contemporary Minimalist</option>
                <option value="Duplex Villa">Duplex Villa (Double Height Living)</option>
                <option value="Budget Economy">Budget Economy</option>
                <option value="Premium Luxury">Premium Luxury (Marble &amp; Glass Facade)</option>
                <option value="Green / Eco-friendly">Green / Solar Passive Architecture</option>
              </select>
            </div>
          </div>

          {/* Amenities Chips */}
          <div className="form-group-block" style={{ marginTop: "16px" }}>
            <label className="field-label">Select Key Amenities &amp; Green Features:</label>
            <div className="amenities-checkbox-grid">
              {AMENITY_OPTIONS.map((amenity) => (
                <div
                  key={amenity.id}
                  className={`amenity-chip ${selectedAmenities.includes(amenity.id) ? "active" : ""}`}
                  onClick={() => toggleAmenity(amenity.id)}
                >
                  <span className="amenity-check">{selectedAmenities.includes(amenity.id) ? "✓" : "+"}</span>
                  <span>{amenity.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group-block" style={{ marginTop: "16px" }}>
            <label className="field-label">Any Additional Special Instructions:</label>
            <textarea
              className="form-textarea-styled"
              name="requirements"
              value={form.requirements}
              onChange={handleChange}
              rows={3}
              placeholder="e.g. Balcony in master bedroom, false ceiling in living room, rainwater harvesting sump..."
            />
          </div>

          {error && <div className="error-banner">⚠️ {error}</div>}

          <div className="step-actions-row">
            <button className="secondary-btn" onClick={() => setStep(1)}>
              ⬅ Back to Step 1
            </button>
            <button className="primary-brand-btn" onClick={handleGenerate} disabled={loading}>
              {loading ? (
                <>
                  <div className="spinner" /> Generating AI Master Plan...
                </>
              ) : (
                <>📄 Generate Complete Master Report</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3 / Report View */}
      {step === 3 && report && (
        <div className="card report-card-wrap">
          <div className="report-header-banner">
            <div className="report-title-box">
              <h2>📋 Your Construction Master Planning Document</h2>
              <p>Generated by NirmanAI • Calibrated with IS 456, IS 875, IS 1893 &amp; NBC 2016</p>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button className="action-pill-btn" onClick={() => setStep(1)}>
                ✏️ Edit Parameters
              </button>
              <button className="action-pill-btn" onClick={handlePrint}>
                🖨️ Print / Save PDF
              </button>
            </div>
          </div>

          <div className="report-markdown-body">
            <MarkdownRenderer content={report} onQuickAction={onAskAI} />
          </div>
        </div>
      )}
    </div>
  );
}
